import { supabase } from '@/integrations/supabase/client';
import { userFacingErrorMessage, userFacingFailureText } from '@/utils/userFacingError';
import type { Locale } from '@financialharmony/i18n';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export interface CompanyUser {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: 'admin' | 'user';
  createdAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: 'admin' | 'user';
  invitedBy: string;
  invitedByEmail: string | null;
  createdAt: string;
  expiresAt: string;
}

export interface CompanyInfo {
  id: string;
  name: string;
  organizationNumber: string;
}

export interface ApiResponse<T> {
  success: boolean;
  error?: string;
  data?: T;
}

/**
 * Calls an action that answers `{ success, error?, ...fields }`. A refusal carries its `error`, or
 * `failureMessage` when it sent none; a failed call shows what `userFacingErrorMessage` allows, or
 * `errorFallback` (FIN-1272).
 */
async function invokeAction<Fields, Result>(
  body: Record<string, unknown>,
  failureMessage: string,
  errorFallback: string,
  pick: (data: Fields) => Result,
): Promise<ApiResponse<Result>> {
  try {
    const { data, error } = await supabase.functions.invoke('user-management-api', { body });

    if (error) {
      return { success: false, error: await userFacingFailureText(error, errorFallback) };
    }

    if (!data.success) {
      return { success: false, error: data.error || failureMessage };
    }

    return { success: true, data: pick(data) };
  } catch (err) {
    return {
      success: false,
      error: userFacingErrorMessage(err, errorFallback)
    };
  }
}

export const userManagementApiClient = {
  /**
   * List all users in a company
   */
  async listUsers(companyId: string): Promise<ApiResponse<CompanyUser[]>> {
    try {
      const { data, error } = await supabase.functions.invoke('user-management-api', {
        body: { action: 'list_users', companyId }
      });

      if (error) {
        return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.companyUsersLoad')) };
      }

      return { success: true, data: data.users };
    } catch (err) {
      return {
        success: false,
        error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.companyUsersLoad'))
      };
    }
  },

  /**
   * Invite a user to join the company
   */
  async inviteUser(companyId: string, email: string, role: 'admin' | 'user' = 'user', redirectBaseUrl?: string, platformRole: 'client' | 'consultant' | 'platform_admin' = 'client', locale?: Locale): Promise<ApiResponse<{ invitationId?: string; addedExistingUser?: boolean; sharedPersonalTaxAccount: boolean }>> {
    return invokeAction(
      { action: 'invite_user', companyId, email, role, redirectBaseUrl, platformRole, locale },
      translateOutsideReact('consultantErrors.fallback.failedToSendInvitation'),
      translateOutsideReact('consultantErrors.fallback.invitationSend'),
      (data: { invitationId?: string; addedExistingUser?: boolean; sharedPersonalTaxAccount?: boolean }) => ({
        invitationId: data.invitationId,
        addedExistingUser: data.addedExistingUser,
        // FIN-1167 EC7: the invitee will see a tax account that is also its owner's personal one.
        sharedPersonalTaxAccount: data.sharedPersonalTaxAccount === true,
      }),
    );
  },

  /**
   * List pending invitations for a company
   */
  async listInvitations(companyId: string): Promise<ApiResponse<Invitation[]>> {
    try {
      const { data, error } = await supabase.functions.invoke('user-management-api', {
        body: { action: 'list_invitations', companyId }
      });

      if (error) {
        return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.pendingInvitationsLoad')) };
      }

      return { success: true, data: data.invitations };
    } catch (err) {
      return {
        success: false,
        error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.pendingInvitationsLoad'))
      };
    }
  },

  /**
   * Accept an invitation using a token
   */
  async acceptInvitation(token: string): Promise<ApiResponse<{ companyId: string }>> {
    return invokeAction(
      { action: 'accept_invitation', token },
      translateOutsideReact('consultantErrors.fallback.failedToAcceptInvitation'),
      translateOutsideReact('consultantErrors.fallback.invitationAccept'),
      (data: { companyId: string }) => ({ companyId: data.companyId }),
    );
  },

  /**
   * Look up a pending invitation by token (no session required)
   */
  async lookupInvitation(token: string): Promise<ApiResponse<{ email: string; companyName: string }>> {
    return invokeAction(
      { action: 'lookup_invitation', token },
      translateOutsideReact('consultantErrors.fallback.invalidInvitation'),
      translateOutsideReact('consultantErrors.fallback.invitationLookup'),
      (data: { email: string; companyName: string }) => ({ email: data.email, companyName: data.companyName }),
    );
  },

  /**
   * Resend the OTP code for a pending invitation (no session required)
   */
  async resendInvitation(token: string, redirectBaseUrl?: string): Promise<ApiResponse<void>> {
    return invokeAction(
      { action: 'resend_invitation', token, redirectBaseUrl },
      translateOutsideReact('consultantErrors.fallback.failedToResendInvitation'),
      translateOutsideReact('consultantErrors.fallback.invitationCodeResend'),
      () => undefined,
    );
  },

  /**
   * Check the user's role in a company
   */
  async checkUserRole(companyId: string): Promise<ApiResponse<'admin' | 'user' | null>> {
    try {
      const { data, error } = await supabase.functions.invoke('user-management-api', {
        body: { action: 'check_user_role', companyId }
      });

      if (error) {
        return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.roleCheck')) };
      }

      return { success: true, data: data.role };
    } catch (err) {
      return {
        success: false,
        error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.roleCheck'))
      };
    }
  },

  /**
   * Revoke an invitation
   */
  async revokeInvitation(invitationId: string): Promise<ApiResponse<void>> {
    return invokeAction(
      { action: 'revoke_invitation', invitationId },
      translateOutsideReact('consultantErrors.fallback.failedToRevokeInvitation'),
      translateOutsideReact('consultantErrors.fallback.invitationRevoke'),
      () => undefined,
    );
  },

  /**
   * Remove a user from a company
   */
  async removeUser(companyId: string, userId: string): Promise<ApiResponse<void>> {
    return invokeAction(
      { action: 'remove_user', companyId, userId },
      translateOutsideReact('consultantErrors.fallback.failedToRemoveUser'),
      translateOutsideReact('consultantErrors.fallback.userRemove'),
      () => undefined,
    );
  },

  /**
   * Check for pending invitations and auto-accept them
   */
  async checkAndAcceptPendingInvitations(): Promise<ApiResponse<{ acceptedCount: number; companyIds: string[] }>> {
    try {
      const { data, error } = await supabase.functions.invoke('user-management-api', {
        body: { action: 'check_and_accept_pending_invitations' }
      });

      if (error) {
        return { success: false, error: await userFacingFailureText(error, translateOutsideReact('consultantErrors.fallback.pendingInvitationsCheck')) };
      }

      return {
        success: true,
        data: {
          acceptedCount: data.acceptedCount || 0,
          companyIds: data.companyIds || []
        }
      };
    } catch (err) {
      return {
        success: false,
        error: userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.pendingInvitationsCheck'))
      };
    }
  },
};
